import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { LoginInput, Privilegio } from '@evolfin/shared';
import { http, inizializzaCsrf } from '@/api/client';

/** Rispecchia il `Principal` dell'API: chi sei e cosa puoi fare. */
export interface Principal {
  id: number;
  /** Nome e cognome dell'utente (vuoti per un principal di servizio). */
  nome: string;
  cognome: string;
  email: string;
  /** Nome del ruolo (dato, non enum). */
  ruolo: string;
  /** Privilegi effettivi (ruolo ∪ extra). */
  privilegi: Privilegio[];
  /** `true` se l'utente ha un'immagine profilo (avatar vs iniziali nella shell). */
  haImmagine: boolean;
  via: 'sessione' | 'api-key';
}

/**
 * Segnaposto booleano (nessun token: la credenziale resta il cookie httpOnly)
 * che ricorda se in questo browser c'era una sessione aperta. Serve solo a
 * distinguere una sessione scaduta da un visitatore che non ha mai fatto login.
 */
const SESSION_MARKER_KEY = 'evolfin.session-active';

function writeSessionMarker(active: boolean): void {
  try {
    if (active) localStorage.setItem(SESSION_MARKER_KEY, '1');
    else localStorage.removeItem(SESSION_MARKER_KEY);
  } catch {
    // Storage non disponibile (es. navigazione privata): si perde solo l'avviso.
  }
}

function readSessionMarker(): boolean {
  try {
    return localStorage.getItem(SESSION_MARKER_KEY) === '1';
  } catch {
    return false;
  }
}

export const useAuthStore = defineStore('auth', () => {
  const principal = ref<Principal | null>(null);
  const caricamento = ref(false);
  /** `true` quando una sessione attiva è stata persa senza un logout volontario. */
  const sessioneScaduta = ref(false);

  const autenticato = computed(() => principal.value !== null);

  /**
   * I privilegi lato client servono SOLO a nascondere ciò che non è
   * accessibile: l'autorizzazione vera resta sempre lato server (guard Nest).
   */
  const ha = (privilegio: Privilegio) => principal.value?.privilegi.includes(privilegio) ?? false;

  async function login(credenziali: LoginInput): Promise<void> {
    caricamento.value = true;
    try {
      // Il login rigenera la sessione e restituisce un token CSRF nuovo: il
      // cookie viene riscritto dal server, l'interceptor axios lo rilegge da sé.
      const { data } = await http.post<{ principal: Principal }>('/api/auth/login', credenziali);
      principal.value = data.principal;
      sessioneScaduta.value = false;
      writeSessionMarker(true);
    } finally {
      caricamento.value = false;
    }
  }

  async function logout(): Promise<void> {
    await http.post('/api/auth/logout');
    principal.value = null;
    writeSessionMarker(false);
    // La sessione distrutta invalida il token CSRF: ne serve uno nuovo per
    // poter rifare il login.
    await inizializzaCsrf();
  }

  /** Ripristina la sessione al reload della pagina (il cookie è httpOnly). */
  async function ripristina(): Promise<void> {
    try {
      const { data } = await http.get<Principal>('/api/auth/me');
      principal.value = data;
      writeSessionMarker(true);
    } catch {
      principal.value = null;
      if (readSessionMarker()) segnaSessioneScaduta();
    }
  }

  /** Il server non riconosce più la sessione che il client credeva aperta. */
  function segnaSessioneScaduta(): void {
    principal.value = null;
    sessioneScaduta.value = true;
    writeSessionMarker(false);
  }

  return {
    principal,
    caricamento,
    autenticato,
    sessioneScaduta,
    ha,
    login,
    logout,
    ripristina,
    segnaSessioneScaduta,
  };
});
